/**
 * Run card v2 (#549) — pure derivation for the printed feeding/medication
 * DAY-GRID. Kept dependency-free and UTC-only so it is unit-testable in
 * isolation and free of the repo's known off-by-one bug class: boarding
 * stay dates arrive as `@db.Date` values serialized to UTC-midnight ISO
 * strings (e.g. "2026-07-17T00:00:00.000Z"), and EVERY label here is built
 * from the UTC components — never `toLocaleDateString()`, which would shift
 * a column to the previous day in a negative-offset timezone.
 *
 * This module owns three concerns the RunCard component composes:
 *   1. buildStayColumns — the ordered day columns of a stay (drop-off day →
 *      pick-up day, inclusive), UTC-safe across month/year boundaries.
 *   2. paginateColumns — Gingr caps a card at 7 day-columns per page and
 *      continues on the next ("Run Card 1 of 2"); this slices the columns.
 *   3. buildGridRows — the feeding (AM/PM) and per-medication (AM/PM) rows
 *      whose cells are left blank for handwritten staff initials. This is
 *      the PACFA paper bridge (#553): CO requires an initialed, timed
 *      administration ledger until digital sign-off is confirmed acceptable.
 */

import { runCardPeriods } from '@ferro/core/modules/care-events/care-schedule.js';

/** One day-column of a stay. All fields derived from a UTC date. */
export interface StayColumn {
  /** UTC-midnight ISO for the day (stable key). */
  readonly iso: string;
  /** Abbreviated weekday, e.g. "Fri". */
  readonly weekday: string;
  /** Short numeric date, e.g. "7/17". */
  readonly dateLabel: string;
}

/** A grid row: one feeding time or one medication administration slot. */
export interface GridRow {
  /** Stable key unique within the card. */
  readonly key: string;
  /** Row label as printed, e.g. "AM: Feed" or "PM: Acid Reducer". */
  readonly label: string;
  /** Whether this row is a feeding or a medication (drives row styling). */
  readonly kind: 'feeding' | 'medication';
}

/** Medication as supplied by the run-card API (subset of the Medication model). */
export interface RunCardMedication {
  readonly id: string;
  readonly name: string;
  readonly dosage: string | null;
  readonly frequency: string | null;
  /** Sawyer 08 meds-feeding (A1) — times staff set; empty/absent ⇒ from `frequency`. */
  readonly slots?: readonly string[];
  readonly instructions: string | null;
  // Sawyer 10 customer-booking-verify-policy (C6) — true when the tenant's lockbox list matches; absent otherwise.
  readonly lockbox?: boolean;
}

/** Feeding info as supplied by the run-card API (subset of Dog). */
export interface RunCardFeeding {
  readonly method: string | null;
  readonly foodType: string | null;
  readonly notes: string | null;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

/** Hard ceiling on stay length we enumerate — guards against a corrupt
 * end-before-start-by-years range blowing up the print page. 62 days = two
 * months, comfortably past any real boarding stay. */
const MAX_STAY_DAYS = 62;

/**
 * Parse a UTC-midnight date from an ISO string, returning the y/m/d parts.
 * Returns null for an unparseable value so callers degrade gracefully
 * (a card with no columns rather than a thrown render).
 */
function utcParts(
  iso: string | null | undefined,
): { readonly y: number; readonly m: number; readonly d: number } | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  const dt = new Date(ms);
  return {
    y: dt.getUTCFullYear(),
    m: dt.getUTCMonth(),
    d: dt.getUTCDate(),
  };
}

/**
 * Enumerate every UTC day from `startISO` to `endISO` inclusive. Uses
 * `Date.UTC(y, m, d + i)` so month- and year-rollover is handled by the
 * Date arithmetic itself (2026-07-31 → 2026-08-01), never by string math.
 *
 * - A null/invalid start yields no columns.
 * - A null/invalid or before-start end collapses to a single day (the
 *   start) — a daycare booking (stay_start == stay_end) is exactly one
 *   column; a corrupt end never renders a negative range.
 * - Ranges longer than MAX_STAY_DAYS are clamped (defensive).
 */
export function buildStayColumns(
  startISO: string | null | undefined,
  endISO: string | null | undefined,
): StayColumn[] {
  const start = utcParts(startISO);
  if (!start) return [];
  const end = utcParts(endISO) ?? start;

  const startMs = Date.UTC(start.y, start.m, start.d);
  const endMs = Date.UTC(end.y, end.m, end.d);
  // Inclusive day count; collapse a reversed/invalid range to 1 day.
  const rawDays = Math.floor((endMs - startMs) / 86_400_000) + 1;
  const days = Math.min(Math.max(rawDays, 1), MAX_STAY_DAYS);

  const columns: StayColumn[] = [];
  for (let i = 0; i < days; i++) {
    const dayMs = Date.UTC(start.y, start.m, start.d + i);
    const dt = new Date(dayMs);
    columns.push({
      iso: dt.toISOString(),
      weekday: WEEKDAYS[dt.getUTCDay()]!,
      dateLabel: `${dt.getUTCMonth() + 1}/${dt.getUTCDate()}`,
    });
  }
  return columns;
}

/**
 * Slice columns into pages of at most `perPage` (Gingr's cap is 7 so a
 * week fits one landscape strip). Always returns at least one page (empty
 * when there are no columns) so the caller can map without a guard.
 */
export function paginateColumns(
  columns: ReadonlyArray<StayColumn>,
  perPage = 7,
): StayColumn[][] {
  const size = Math.max(1, Math.floor(perPage));
  if (columns.length === 0) return [[]];
  const pages: StayColumn[][] = [];
  for (let i = 0; i < columns.length; i += size) {
    pages.push(columns.slice(i, i + size));
  }
  return pages;
}

/**
 * The administration periods a medication needs. Sawyer A1 (lane 08,
 * refuter C-6): there is ONE rule for when a dose is due, in core
 * (`care-schedule.ts`). Times staff set print as-is (AM / Noon / PM /
 * Bedtime / PRN); without them the frequency text decides exactly as the
 * card always has (twice → AM + PM, as needed → PRN, evening → PM, anything
 * else → one AM box, blank → one unlabelled row so the med is never dropped
 * from the paper ledger).
 */
function periodsFor(med: RunCardMedication): ReadonlyArray<string> {
  return runCardPeriods(med);
}

/** Prefix a med label with its period, e.g. "AM: Acid Reducer". */
function medLabel(period: string, med: RunCardMedication): string {
  const dose = med.dosage && med.dosage.trim().length > 0 ? ` (${med.dosage.trim()})` : '';
  const name = `${med.name.trim()}${dose}`;
  return period ? `${period}: ${name}` : name;
}

/**
 * Build the full ordered row set for a card's grid: two feeding rows
 * (AM/PM — always present for a boarding/daycare dog, matching Gingr) then
 * one row per medication administration slot. Cells are rendered empty by
 * the component for handwritten initials. The feeding DETAIL (what/how
 * much) is surfaced separately in the card header via `formatFeeding`, not
 * in these row labels, which stay terse to match Gingr's grid.
 */
export function buildGridRows(
  medications: ReadonlyArray<RunCardMedication>,
): GridRow[] {
  const rows: GridRow[] = [
    { key: 'feed-am', label: 'AM: Feed', kind: 'feeding' },
    { key: 'feed-pm', label: 'PM: Feed', kind: 'feeding' },
  ];
  for (const med of medications) {
    const periods = periodsFor(med);
    periods.forEach((period, idx) => {
      rows.push({
        key: `med-${med.id}-${idx}`,
        label: medLabel(period, med),
        kind: 'medication',
      });
    });
  }
  return rows;
}

/**
 * Human age from a birth-date ISO, as "N Years, M Months" (Gingr parity).
 * UTC-based to stay off-by-one-free. Returns null for missing/future/
 * invalid input.
 */
export function formatAge(
  birthDateISO: string | null | undefined,
  nowISO: string | null | undefined = new Date().toISOString(),
): string | null {
  const b = utcParts(birthDateISO);
  const n = utcParts(nowISO);
  if (!b || !n) return null;
  let months = (n.y - b.y) * 12 + (n.m - b.m);
  if (n.d < b.d) months -= 1;
  if (months < 0) return null;
  const years = Math.floor(months / 12);
  const rem = months % 12;
  const parts: string[] = [];
  if (years > 0) parts.push(`${years} ${years === 1 ? 'Year' : 'Years'}`);
  parts.push(`${rem} ${rem === 1 ? 'Month' : 'Months'}`);
  return parts.join(', ');
}

/**
 * "Male/Altered" style descriptor from sex + alteration state. Mirrors the
 * Dog model's dual representation: `alteredStatus` ('intact'|'spayed'|
 * 'neutered') is authoritative when present, else the legacy `isNeutered`
 * boolean. Returns null when nothing is known (never prints a bare slash).
 */
export function formatSexAltered(
  sex: string | null | undefined,
  alteredStatus: string | null | undefined,
  isNeutered: boolean | null | undefined,
): string | null {
  const sexPart = sex ? sex.charAt(0).toUpperCase() + sex.slice(1).toLowerCase() : null;
  let alteredPart: string | null = null;
  if (alteredStatus && alteredStatus.trim().length > 0) {
    alteredPart = alteredStatus.toLowerCase() === 'intact' ? 'Intact' : 'Altered';
  } else if (typeof isNeutered === 'boolean') {
    alteredPart = isNeutered ? 'Altered' : 'Intact';
  }
  if (sexPart && alteredPart) return `${sexPart}/${alteredPart}`;
  return sexPart ?? alteredPart ?? null;
}

/**
 * A one-line feeding summary for the card header, e.g.
 * "Scheduled · Dry — 1 cup AM, 1 cup PM". Method + food type are
 * title-cased and underscores humanized; the free-text notes follow an
 * em-dash. Returns null when nothing is on file (the card then shows an
 * ask-owner placeholder).
 */
export function formatFeeding(feeding: RunCardFeeding | null): string | null {
  if (!feeding) return null;
  const humanize = (v: string | null): string | null => {
    if (!v || v.trim().length === 0) return null;
    const s = v.trim().replace(/_/g, ' ');
    return s.charAt(0).toUpperCase() + s.slice(1);
  };
  const tags = [humanize(feeding.method), humanize(feeding.foodType)].filter(
    (t): t is string => t !== null,
  );
  const notes = feeding.notes && feeding.notes.trim().length > 0 ? feeding.notes.trim() : null;
  const head = tags.join(' · ');
  if (head && notes) return `${head} — ${notes}`;
  return head || notes || null;
}

/**
 * Ted Item 35 — the allergy text a run card should shout about, or null.
 *
 * The Gingr mirror already drops its "None" / "N/A" non-answers at import
 * (gingr-api-client.ts `gingrCareText`), but a value typed at the desk never
 * passes through that filter. A desk-typed "None" must not print the red
 * ALLERGIES block: a warning that fires on dogs with no allergy teaches
 * staff to read past the one that matters. Deliberately narrow — only a
 * whole-field "none"-style answer is dropped; anything carrying an
 * instruction ("None, but no chicken treats") still prints.
 */
const ALLERGY_NON_ANSWER =
  /^(none|no|n\/?a|nka|nkda|nope|unknown|none known|no known allergies|no allergies|none listed)[.!]?$/i;

export function formatAllergies(text: string | null | undefined): string | null {
  if (!text) return null;
  const t = text.trim();
  if (t.length === 0) return null;
  return ALLERGY_NON_ANSWER.test(t) ? null : t;
}

/**
 * #597 / B7 — the provenance line under the card's feeding + MEDS lines:
 * "Medication and feeding last imported from Gingr Oct 5". Printed only for
 * the parts of the card whose text actually came from the daily Gingr
 * mirror, so staff can tell a Gingr instruction (and how old it is) from
 * one typed at the desk. The date is the last sync that READ the value, not
 * the day it changed — if the mirror stops, this date stops moving, which
 * is the point. UTC like the rest of the card's date handling; the mirror
 * runs at 12:00 UTC (06:00 Denver), so the day matches the local day.
 *
 * `medication` / `feeding` are the import times for the parts the card is
 * really printing (the caller passes null for a part it isn't showing, e.g.
 * feeding when a check-in override replaced the profile note). Null when
 * neither applies.
 */
export function formatGingrImportCaption(args: {
  medication: string | null | undefined;
  feeding: string | null | undefined;
}): string | null {
  const day = (iso: string | null | undefined): string | null => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  };
  const med = day(args.medication);
  const feed = day(args.feeding);
  if (med && feed) {
    return med === feed
      ? `Medication and feeding last imported from Gingr ${med}`
      : `Medication last imported from Gingr ${med} · feeding ${feed}`;
  }
  if (med) return `Medication last imported from Gingr ${med}`;
  if (feed) return `Feeding last imported from Gingr ${feed}`;
  return null;
}

/** Convert an "HH:MM" 24h clock string to "h:mm am/pm". Passthrough for junk. */
function formatClock(time: string | null | undefined): string | null {
  if (!time) return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(time.trim());
  if (!m) return null;
  let hour = Number(m[1]);
  const minute = m[2];
  const suffix = hour >= 12 ? 'pm' : 'am';
  hour = hour % 12;
  if (hour === 0) hour = 12;
  return `${hour}:${minute} ${suffix}`;
}

/** Full weekday+date for the stay-window header line, e.g. "Fri, 07/17". */
function longDateLabel(iso: string): string | null {
  const p = utcParts(iso);
  if (!p) return null;
  const dt = new Date(Date.UTC(p.y, p.m, p.d));
  const mm = String(p.m + 1).padStart(2, '0');
  const dd = String(p.d).padStart(2, '0');
  return `${WEEKDAYS[dt.getUTCDay()]}, ${mm}/${dd}`;
}

/**
 * The header stay-window string, Gingr-style:
 *   "Fri, 07/17, 11:51 am - Mon, 07/27, 4:00 pm"
 * Times are optional; a single-day stay with no times collapses to the day.
 * UTC-safe throughout.
 */
export function formatStayWindow(args: {
  readonly startISO: string | null | undefined;
  readonly endISO: string | null | undefined;
  readonly dropOffTime: string | null | undefined;
  readonly pickUpTime: string | null | undefined;
}): string {
  const startDate = args.startISO ? longDateLabel(args.startISO) : null;
  const endDate = args.endISO ? longDateLabel(args.endISO) : null;
  const dropTime = formatClock(args.dropOffTime);
  const pickTime = formatClock(args.pickUpTime);

  const startPart = [startDate, dropTime].filter(Boolean).join(', ');
  const endPart = [endDate, pickTime].filter(Boolean).join(', ');

  if (startPart && endPart && startPart !== endPart) return `${startPart} – ${endPart}`;
  return startPart || endPart || '—';
}
